import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS as LMS_ENDPOINTS } from '../../../Utils/Endpoints';
import { API_BASE } from '../../../../../api';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './EstudiantesPremiosModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Compras de Premios";
const endpoint = LMS_ENDPOINTS.estudiantesPremios;
const endpointById = LMS_ENDPOINTS.estudiantesPremiosById;
const modelo = Models.estudiantesPremios;

const maps = {
    mapCreate: (form) => ({
        idEstudiante: Number(form.idEstudiante),
        idPremio: Number(form.idPremio),
        costo: Number(form.costo),
        fechaCompra: form.fechaCompra,
        activo: form.activo !== undefined ? form.activo : true,
    }),
    mapUpdate: (id, form) => ({
        idEstudiantePremios: Number(id),
        idEstudiante: Number(form.idEstudiante),
        idPremio: Number(form.idPremio),
        costo: Number(form.costo),
        fechaCompra: form.fechaCompra,
        activo: form.activo !== undefined ? form.activo : true,
    }),
};

export default function Catalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [estudiantes, setEstudiantes] = useState([]);
    const [premios, setPremios] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [resCompras, resEstudiantes, resPremios, resEmpleados, resPersonas] = await Promise.all([
                axios.get(LMS_ENDPOINTS.estudiantesPremiosDetalles),
                axios.get(LMS_ENDPOINTS.estudiante),
                axios.get(LMS_ENDPOINTS.catPremios),
                axios.get(`${API_BASE}/Empleados`),
                axios.get(`${API_BASE}/Personas`),
            ]);

            setRows((resCompras.data || []).map((x) => modelo(x)));
            setEstudiantes((resEstudiantes.data || []).map(Models.estudiante));
            setPremios((resPremios.data || []).map(Models.premios));
            setEmpleados((resEmpleados.data || []).map(Models.empleado));
            setPersonas((resPersonas.data || []).map(Models.personas));
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar información');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    // Helper para obtener nombre del estudiante
    const getEstudianteNombre = (idEstudiante) => {
        const estudiante = estudiantes.find(e => Number(e.idEstudiante) === Number(idEstudiante));
        if (!estudiante) return 'Desconocido';

        const idEmpleado = estudiante.idEmpleado || estudiante.IdEmpleado;
        const empleado = empleados.find(emp => Number(emp.idEmpleado) === Number(idEmpleado));
        if (!empleado) return `Estudiante #${idEstudiante}`;

        const idPersona = empleado.idPersona || empleado.IdPersona;
        const persona = personas.find(p => Number(p.idPersona) === Number(idPersona));
        if (!persona) return `Estudiante #${idEstudiante}`;

        const nombre = persona.nombre || persona.Nombre || '';
        const ap = persona.apellidoPaterno || persona.ApellidoPaterno || '';
        const am = persona.apellidoMaterno || persona.ApellidoMaterno || '';

        return `${nombre} ${ap} ${am}`.trim() || `Estudiante #${idEstudiante}`;
    };

    // Helper para obtener nombre del premio
    const getPremioNombre = (idPremio) => {
        const premio = premios.find(p => p.idPremio === idPremio);
        return premio ? premio.nombre : `Premio #${idPremio}`;
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const estudianteNombre = (r.estudianteNombre || getEstudianteNombre(r.idEstudiante)).toLowerCase();
            const premioNombre = (r.premioNombre || getPremioNombre(r.idPremio)).toLowerCase();
            const costo = String(r.costo || '');
            return (
                estudianteNombre.includes(q) ||
                premioNombre.includes(q) ||
                costo.includes(q)
            );
        });
    }, [rows, query, estudiantes, premios, empleados, personas]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (id) => {
        const r = rows.find(x => x.idEstudiantePremios === id);
        if (!r) {
            triggerAlert('No se encontró la fila en memoria.');
            return;
        }
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            if (mode === 'new') {
                await axios.post(endpoint, maps.mapCreate(payload));
            } else {
                await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch {
            return '—';
        }
    };

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>

                    <input
                        className="roles-search"
                        placeholder="Buscar por estudiante, premio o costo…"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Estudiante</th>
                                    <th>Premio</th>
                                    <th>Costo</th>
                                    <th>Fecha de Compra</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => (
                                    <tr key={r.idEstudiantePremios}>
                                        <td><strong>{r.estudianteNombre || getEstudianteNombre(r.idEstudiante)}</strong></td>
                                        <td>{r.premioNombre || getPremioNombre(r.idPremio)}</td>
                                        <td>{r.costo} monedas</td>
                                        <td>{formatDate(r.fechaCompra)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--edit"
                                                onClick={() => handleEdit(r.idEstudiantePremios)}
                                                aria-label="Editar"
                                                title="Editar"
                                            >
                                                <img src={sigmaEdit} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                className="rk-iconbtn rk-iconbtn--del"
                                                onClick={() => {
                                                    const name = `${getEstudianteNombre(r.idEstudiante)} - ${getPremioNombre(r.idPremio)}`;
                                                    askDelete(r.idEstudiantePremios, name);
                                                }}
                                                aria-label="Borrar"
                                                title="Borrar"
                                            >
                                                <img src={sigmaDelete} alt="" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={6}>Sin resultados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn
                            label="‹"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            estudiantes={estudiantes}
                            premios={premios}
                            empleados={empleados}
                            personas={personas}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
